import { Box, Button, ButtonBase, Stack, Typography } from '@mui/material';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAdvisorAccess } from '../../context/AdvisorAccessContext';
import { IconMap } from '../Sidebar/IconHelper';

interface IconButtonHoverWrapperProps {
  item: {
    href: string;
    label: string;
    icon: React.ComponentType<any>;
  };
  isActive: boolean;
  navigate: (path: string) => void;
}

function IconButtonHoverWrapper({ item, isActive, navigate }: IconButtonHoverWrapperProps) {
  const [hovered, setHovered] = React.useState(false);

  const shouldHighlight = isActive || hovered;

  const ICON_SIZE = 32;
  const VERTICAL_PADDING = '0.625rem'; // ~10px
  const HORIZONTAL_PADDING = '0.75rem';
  const PRIMARY_COLOR = '#123833';
  const BAR_BACKGROUND = '#FFE59D';

  return (
    <ButtonBase
      disableRipple
      onClick={() => navigate(item.href)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        borderRadius: '16px',
        py: VERTICAL_PADDING,
        px: HORIZONTAL_PADDING,
        transition: 'background-color 0.2s ease, color 0.2s ease',
        backgroundColor: 'transparent',
        gap: 0.5,
        pointerEvents: 'auto',
      }}
    >
      <Box
        sx={{
          width: ICON_SIZE,
          height: ICON_SIZE,
          borderRadius: '50%',
          backgroundColor: BAR_BACKGROUND,
          color: PRIMARY_COLOR,
          border: `2px solid ${shouldHighlight ? PRIMARY_COLOR : 'transparent'}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease',
        }}
      >
        <item.icon sx={{ fontSize: 20 }} />
      </Box>
      <Typography
        sx={{
          color: PRIMARY_COLOR,
          fontSize: 12,
          fontWeight: isActive ? 700 : 400,
          lineHeight: 1.2,
        }}
      >
        {item.label}
      </Typography>
    </ButtonBase>
  );
}

export default function AdvisorModeBar() {
  const { advisedFor, exitAdvisedFor, refreshAdvisedFor } = useAdvisorAccess();
  const navigate = useNavigate();
  const location = useLocation();
  const expirationTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = React.useRef(true);

  const handleExpiration = React.useCallback(async () => {
    // Wait 2 seconds after expiration
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Check if component is still mounted
    if (!isMountedRef.current) {
      return;
    }

    // Call getAdvisedFor again (loadAdvisedFor will handle redirect if no advisor context)
    await refreshAdvisedFor();
  }, [refreshAdvisedFor]);

  React.useEffect(() => {
    isMountedRef.current = true;
    // Clear timeout on unmount
    return () => {
      isMountedRef.current = false;
      if (expirationTimeoutRef.current) {
        clearTimeout(expirationTimeoutRef.current);
        expirationTimeoutRef.current = null;
      }
    };
  }, []);

  React.useEffect(() => {
    if (!advisedFor || !advisedFor.expiresAt) {
      return;
    }

    // Clear any existing timeout
    if (expirationTimeoutRef.current) {
      clearTimeout(expirationTimeoutRef.current);
      expirationTimeoutRef.current = null;
    }

    const now = Date.now();
    const expiresAt = advisedFor.expiresAt;
    const timeUntilExpiration = expiresAt - now;

    if (timeUntilExpiration <= 0) {
      // Already expired, handle immediately
      handleExpiration();
      return;
    }

    // Set timeout for when expiration occurs
    expirationTimeoutRef.current = setTimeout(() => {
      handleExpiration();
    }, timeUntilExpiration);

    // Cleanup function
    return () => {
      if (expirationTimeoutRef.current) {
        clearTimeout(expirationTimeoutRef.current);
        expirationTimeoutRef.current = null;
      }
    };
  }, [advisedFor, handleExpiration]);

  if (!advisedFor) return null;

  const handleExit = async () => {
    // Clear expiration timeout
    if (expirationTimeoutRef.current) {
      clearTimeout(expirationTimeoutRef.current);
      expirationTimeoutRef.current = null;
    }
    await exitAdvisedFor();
    // Optionally refresh current page
    window.location.reload();
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 32,
        right: { xs: '1rem', md: '1.875rem' },
        zIndex: 1300,
        pointerEvents: 'none',
      }}
    >
      {/* Capsule container styled to match tones from the app */}
      <Box
        sx={{
          width: 'fit-content',
          boxSizing: 'border-box',
          border: '1px solid #f3cf70',
          backgroundColor: '#FFE59D',
          borderRadius: '9999px',
          px: '28px',
          py: 0.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 2,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          pointerEvents: 'auto',
        }}
      >
        {/* Left side: label + name on separate lines + circular icon placeholders */}
        <Stack direction="column" spacing={0.5} alignItems="flex-start">
          <Typography sx={{ fontWeight: 700, color: '#123833' }}>Advisor view:</Typography>
          <Typography sx={{ color: '#123833' }}>{advisedFor.name}</Typography>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center">
          {[
            {
              href: '/advisor/opportunities',
              label: 'New',
              icon: IconMap.Funds.iconElement,
              matchPattern: (pathname: string) => pathname.startsWith('/advisor/opportunities'),
            },
            {
              href: '/advisor/portfolio',
              label: 'Portfolio',
              icon: IconMap.Portfolio.iconElement,
              matchPattern: (pathname: string) => pathname.startsWith('/advisor/portfolio'),
            },
          ].map((item) => {
            const isActive = item.matchPattern(location.pathname);
            return (
              <IconButtonHoverWrapper
                key={item.label}
                item={item}
                isActive={isActive}
                navigate={navigate}
              />
            );
          })}
        </Stack>

        {/* Right side: Exit button */}
        <Button
          variant="contained"
          onClick={handleExit}
          sx={{
            minWidth: 80,
            borderRadius: '9999px',
            px: 2,
            py: 0.5,
            backgroundColor: '#123833',
            color: '#FFFFFF',
            ml: 3,
            '&:hover': {
              backgroundColor: '#0f2d2a',
            },
          }}
        >
          Exit
        </Button>
      </Box>
    </Box>
  );
}
